// The boards of the call (ADR-0007): which are open and what is on them — the server says — and what the
// user does to them: opens one, draws, writes, moves and erases, undoes and does again, points, says who
// draws, clears, closes. An act goes to the server at once and shows at once, on top of the board as the
// server's acts left it, till it comes back in their order (board.ts). A pen's path goes as it is drawn:
// its first point, then its new points every `FLUSH` ms; the page draws the user's own path whole, from
// here, without waiting.
import {
  addStep,
  aged,
  copied,
  done,
  eraseStep,
  MOST_PEN,
  moveStep,
  newBoard,
  refused,
  shown,
  stepped,
  type Act,
  type Board,
  type Mode,
  type Shape,
  type Step,
} from "./board.ts";
import { send } from "./net.ts";
import type { Server } from "./protocol.ts";
import { changed, isHost, isModerator, state } from "./store.ts";

/** How often a pen's new points go, ms; how close a point may be to the one before and still be kept, a
 * board's units; how long another's pointer shows after it stopped, ms. */
export const FLUSH = 50;
const STEP = 2;
export const STILL = 3000;

/** The acts' numbers the server sends back (a random start: two pages of one user do not take each
 * other's); the shapes' ids — this page's prefix, then a count — one on a board. */
let nonce = Math.floor(Math.random() * 1e9);
const prefix = Math.random().toString(36).slice(2, 8);
let count = 0;
/** The nonce of the board the user opened last: it shows first when it comes. */
let opening = -1;

/** The pen's path the user draws now: its board and shape, all its points, how many of them went. */
type Stroke = { board: number; shape: Shape; points: number[]; sent: number; timer: number };
let stroke: Stroke | null = null;

/** A new shape's id. */
export function newId(): string {
  count += 1;
  return `${prefix}${count.toString(36)}`;
}

/** Whether the user is the host or a moderator: says who draws, closes and clears any board. */
export function hosts(): boolean {
  return isHost() || isModerator(state.user);
}

/** Whether the user draws on board `b`: everyone does, or the user is among the hosts. */
export function mayDraw(b: Board): boolean {
  return b.mode === "all" || hosts();
}

/** Whether the user closes and clears board `b`: its author, the host and moderators. */
export function mayClose(b: Board): boolean {
  return b.author === state.user || hosts();
}

/** The boards in the order they were opened. */
export function boardsShown(): Board[] {
  return [...state.boards.values()].sort((a, b) => a.number - b.number);
}

/** The shapes board `b` shows: the server's, the user's acts on top — but the path the user draws now,
 * which the page draws whole (`drawing`). */
export function shapesOf(b: Board): Shape[] {
  const all = shown(b, state.user);
  const s = stroke;
  return s !== null && s.board === b.number ? all.filter((h) => h.id !== s.shape.id) : all;
}

/** The path the user draws now on board `number`, whole; `null` if none. */
export function drawing(number: number): Shape | null {
  const s = stroke;
  if (s === null || s.board !== number) return null;
  return { ...s.shape, points: Int16Array.from(s.points) };
}

/** The call began or ended: no boards till the server says (it sends those of the call to one who
 * comes). */
export function noBoards(): void {
  state.boards = new Map();
  stroke = null;
}

// ---------------------------------------------------------------- the server

/** Whether a message of channel `channel` is of the call's boards. */
function ours(channel: number): boolean {
  return state.call !== null && state.call.channel === channel;
}

/** The boards of the call: those not among them are gone. */
export function onBoards(m: Server<"boards">): void {
  if (!ours(m.channel)) return;
  const next = new Map<number, Board>();
  for (const i of m.boards) {
    const b = state.boards.get(i.board) ?? newBoard(i.board, i.author, i.mode);
    next.set(i.board, { ...b, author: i.author, mode: i.mode });
  }
  state.boards = next;
  changed();
}

/** A copy of a board's shapes, or a piece of one. */
export function onBoardState(m: Server<"boardState">): void {
  if (!ours(m.channel)) return;
  const b = state.boards.get(m.board) ?? newBoard(m.board, 0, "all");
  state.boards.set(m.board, copied(b, m.from, m.shapes, m.seq));
  changed();
}

/** An act done to a board, in the server's order: the user's own comes back. */
export function onBoardDone(m: Server<"board">): void {
  if (!ours(m.channel)) return;
  if (m.act.type === "close") {
    state.boards.delete(m.board);
    if (stroke?.board === m.board) stroke = null;
    return changed();
  }
  const b = state.boards.get(m.board) ?? (m.act.type === "open" ? newBoard(m.board, m.user, "all") : null);
  if (b === null) return;
  state.boards.set(m.board, done(b, m.act, m.seq, m.user, m.nonce, state.user, performance.now()));
  if (m.act.type === "open" && m.user === state.user && m.nonce === opening) state.boardShown = m.board;
  changed();
}

/** An act of the user's the server refused: it shows no more (meeting.ts says why). */
export function onBoardRefused(m: Server<"boardRefused">): void {
  if (!ours(m.channel)) return;
  const b = state.boards.get(m.board);
  if (b !== undefined) state.boards.set(m.board, refused(b, m.nonce));
  if (stroke?.board === m.board) stroke = null;
  changed();
}

/** The boards without the user's acts the server lost and the pointers that stopped: the page asks now
 * and then. */
export function ageBoards(): void {
  const now = performance.now();
  let any = false;
  for (const [n, b] of state.boards) {
    const a = aged(b, now, STILL);
    if (a !== b) {
      state.boards.set(n, a);
      any = true;
    }
  }
  if (any) changed();
}

// ---------------------------------------------------------------- the user's acts

/** Act `a` on board `number` of the call: it goes, and shows till it comes back (`track`). */
function act(number: number, a: Act, track: boolean): void {
  const call = state.call;
  if (call === null) return;
  nonce += 1;
  send({ type: "board", channel: call.channel, board: number, nonce, act: a });
  const b = state.boards.get(number);
  if (!track || b === undefined) return;
  state.boards.set(number, { ...b, pending: b.pending.concat({ nonce, act: a, at: performance.now() }) });
  changed();
}

/** A new board: it shows first when it comes. */
export function openBoard(): void {
  act(0, { type: "open" }, false);
  opening = nonce;
}

export function closeBoard(number: number): void {
  act(number, { type: "close" }, false);
}

export function clearBoard(number: number): void {
  act(number, { type: "clear" }, true);
}

export function setMode(number: number, mode: Mode): void {
  act(number, { type: "mode", mode }, true);
}

/** Shape `h` on board `number`, which the user may undo. */
export function addShape(number: number, h: Shape): void {
  act(number, { type: "add", shapes: [h] }, true);
  record(number, addStep([h]));
}

/** Shapes `ids` of board `number` moved by `dx`, `dy`. */
export function moveShapes(number: number, ids: string[], dx: number, dy: number): void {
  if (ids.length === 0 || (dx === 0 && dy === 0)) return;
  act(number, { type: "move", ids, dx, dy }, true);
  record(number, moveStep(ids, dx, dy));
}

/** Shapes `ids` of board `number` erased: undoing brings them back as they were. */
export function eraseShapes(number: number, ids: string[]): void {
  const b = state.boards.get(number);
  if (b === undefined || ids.length === 0) return;
  const gone = new Set(ids);
  const shapes = shown(b, state.user).filter((h) => gone.has(h.id));
  if (shapes.length === 0) return;
  act(number, { type: "erase", ids: shapes.map((h) => h.id) }, true);
  record(number, eraseStep(shapes));
}

/** The user's step on board `number`, to undo. */
function record(number: number, s: Step): void {
  const b = state.boards.get(number);
  if (b !== undefined) state.boards.set(number, stepped(b, s));
}

/** Undoes the user's last step on board `number`. */
export function undo(number: number): void {
  const b = state.boards.get(number);
  const s = b?.undo.at(-1);
  if (b === undefined || s === undefined) return;
  state.boards.set(number, { ...b, undo: b.undo.slice(0, -1), redo: b.redo.concat(s) });
  for (const a of s.undo) act(number, a, true);
}

/** Does again the user's last step undone on board `number`. */
export function redo(number: number): void {
  const b = state.boards.get(number);
  const s = b?.redo.at(-1);
  if (b === undefined || s === undefined) return;
  state.boards.set(number, { ...b, redo: b.redo.slice(0, -1), undo: b.undo.concat(s) });
  for (const a of s.redo) act(number, a, true);
}

/** The user's pointer at `x`, `y` on board `number` — or gone (-1). Nothing keeps it: it is not tracked. */
export function point(number: number, x: number, y: number): void {
  act(number, { type: "pointer", x, y }, false);
}

// ---------------------------------------------------------------- the pen

/** A pen's path begins at `x`, `y` on board `number`: its first point goes at once. */
export function penDown(number: number, x: number, y: number, color: number, width: number): void {
  penUp();
  const points = Int16Array.of(x, y);
  const shape: Shape = { id: newId(), kind: "pen", points, color, width, text: "", size: 0, by: 0 };
  act(number, { type: "add", shapes: [shape] }, true);
  stroke = { board: number, shape, points: [x, y], sent: 2, timer: setInterval(flush, FLUSH) as unknown as number };
  changed();
}

/** The pen's path goes on to `x`, `y` — unless it is that close to the point before, or long enough. */
export function penTo(x: number, y: number): void {
  const s = stroke;
  if (s === null || s.points.length >= 2 * MOST_PEN) return;
  const [px, py] = [s.points[s.points.length - 2], s.points[s.points.length - 1]];
  if (Math.hypot(x - px, y - py) < STEP) return;
  s.points.push(x, y);
  changed();
}

/** The pen's points not sent yet go. */
function flush(): void {
  const s = stroke;
  if (s === null || s.sent >= s.points.length) return;
  act(s.board, { type: "extend", id: s.shape.id, points: Int16Array.from(s.points.slice(s.sent)) }, true);
  s.sent = s.points.length;
}

/** The pen's path ends: its last points go, and the whole of it is a step to undo. */
export function penUp(): void {
  const s = stroke;
  if (s === null) return;
  flush();
  clearInterval(s.timer);
  stroke = null;
  record(s.board, addStep([{ ...s.shape, points: Int16Array.from(s.points) }]));
  changed();
}
